import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { differenceInDays, parseISO, addMonths } from 'date-fns';

export interface ContentCardFull {
  id: string;
  section: string;
  category: string;
  title: string;
  summary: string;
  body: string;
  icon: string;
  link_label: string | null;
  link_url: string | null;
  sort_order: number;
  last_reviewed_at: string;
  review_frequency_months: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type HealthStatus = 'green' | 'yellow' | 'red';

export interface SectionHealth {
  section: string;
  label: string;
  icon: string;
  total: number;
  status: HealthStatus;
  overdue: ContentCardFull[];
  dueSoon: ContentCardFull[];
  upToDate: ContentCardFull[];
}

export function getCardDueInfo(card: ContentCardFull) {
  const reviewedAt = parseISO(card.last_reviewed_at);
  const dueDate = addMonths(reviewedAt, card.review_frequency_months);
  const today = new Date();
  const daysUntilDue = differenceInDays(dueDate, today);
  const isOverdue = daysUntilDue < 0;
  const isDueSoon = !isOverdue && daysUntilDue <= 30;
  return { dueDate, daysUntilDue, isOverdue, isDueSoon };
}

const SECTION_META: Record<string, { label: string; icon: string }> = {
  'employment-rights': { label: 'Employment Rights', icon: '⚖️' },
  'work-self-employed': { label: 'Work Self-employed', icon: '💼' },
  'tenant-rights': { label: 'Tenant Rights', icon: '🏠' },
  'home-rights': { label: 'Home Rights', icon: '🔑' },
  'benefits-info': { label: 'Benefits Info', icon: '💷' },
  'health-info': { label: 'Health Info', icon: '🩺' },
};

export function useContentHealth() {
  const [cards, setCards] = useState<ContentCardFull[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCards = async () => {
    const { data } = await supabase
      .from('content_cards')
      .select('*')
      .order('section')
      .order('category')
      .order('sort_order');
    setCards((data as ContentCardFull[]) || []);
    setLoading(false);
  };

  useEffect(() => { fetchCards(); }, []);

  const sections: SectionHealth[] = [];
  const sectionMap = new Map<string, ContentCardFull[]>();
  
  for (const card of cards) {
    if (!sectionMap.has(card.section)) sectionMap.set(card.section, []);
    sectionMap.get(card.section)!.push(card);
  }

  for (const [section, sectionCards] of sectionMap) {
    const meta = SECTION_META[section] || { label: section, icon: '📋' };
    const overdue: ContentCardFull[] = [];
    const dueSoon: ContentCardFull[] = [];
    const upToDate: ContentCardFull[] = [];

    for (const card of sectionCards) {
      const { isOverdue, isDueSoon } = getCardDueInfo(card);
      if (isOverdue) overdue.push(card);
      else if (isDueSoon) dueSoon.push(card);
      else upToDate.push(card);
    }

    const status: HealthStatus = overdue.length > 0 ? 'red' : dueSoon.length > 0 ? 'yellow' : 'green';

    sections.push({
      section,
      label: meta.label,
      icon: meta.icon,
      total: sectionCards.length,
      status,
      overdue,
      dueSoon,
      upToDate,
    });
  }

  const totalDue = sections.reduce((sum, s) => sum + s.overdue.length + s.dueSoon.length, 0);
  const totalOverdue = sections.reduce((sum, s) => sum + s.overdue.length, 0);

  return { sections, cards, totalDue, totalOverdue, loading, refetch: fetchCards };
}
